import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Overlay } from '~/shared/components';
import {
    StyledImageClicker,
    StyledImageWrapper,
    StyledFallbackImageWrapper,
    StyledTags,
    ProductTagsWrapper,
    StyledEnlargedImageWrapper,
} from './styled';
import { queries, queriesIsolated } from '~/theme/breakpoints';
import { ConditionalWrap, Visibility } from '~/shared/utils/jsx';
import { useRouter } from 'next/router';
import { ProductImage } from '~/shared/components/ProductImage';
import { ProductImageViewModel } from '~/lib/data-contract';
import { ProductTags } from '~/shared/components/ProductTags';
import { useProduct } from '~/shared/utils/product';
import { useMedia } from 'react-use';

export type Props = {
    images?: ProductImageViewModel[] | null;
    alt?: string;
};

type ScrollState = {
    block: ScrollLogicalPosition;
    target: HTMLElement;
};

export const ImageDisplay = ({ images, alt }: Props) => {
    const [imageIndex, setImageIndex] = useState<number | null>(null);
    const [enlargeImages, setEnlargeImages] = useState<boolean>(false);
    const { data: product } = useProduct();
    const scrollStateRef = useRef<ScrollState | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const { asPath } = useRouter();
    const isSmallDevice = !useMedia(queries.lg, false);

    const onOverlayLoad = () => {
        const images = overlayRef.current?.querySelectorAll('img');

        if (images?.length && imageIndex !== null) {
            images[imageIndex]?.scrollIntoView();
        }
    };
    const onOverlayDismiss = useCallback(() => setImageIndex(null), [setImageIndex]);

    const setScrollPosition = useCallback((event: React.MouseEvent<HTMLElement>) => {
        const target = event.currentTarget;
        const pos = event.pageY - target.getBoundingClientRect().top;
        const percent = (pos / target.clientHeight) * 100;

        let block: ScrollLogicalPosition = 'center';
        if (percent <= 25) {
            block = 'start';
        } else if (percent >= 75) {
            block = 'end';
        }

        scrollStateRef.current = {
            block,
            target,
        };
    }, []);

    // Reset wrapper scroll on variant change
    useEffect(() => {
        if (wrapperRef.current) {
            wrapperRef.current.scrollTo(0, 0);
        }
    }, [asPath]);

    useEffect(() => {
        const { target, block } = scrollStateRef?.current ?? {};
        target?.scrollIntoView({ block });
    }, [enlargeImages]);

    return (
        <>
            <ProductTagsWrapper>
                <StyledTags>
                    <ProductTags
                        productTags={product?.imageTags}
                        selectFrom="top"
                        layout="spaced-list"
                    />
                </StyledTags>
            </ProductTagsWrapper>
            <StyledImageWrapper ref={wrapperRef}>
                {images?.length ? (
                    <>
                        {images?.map((image, index) => (
                            <StyledImageClicker
                                aria-label={image.label}
                                key={image.url}
                                onClick={() => setImageIndex(index)}
                            >
                                <ProductImage
                                    image={image}
                                    skeletonShade={index < 2 ? 'none' : 'light'}
                                    sizes={`${queriesIsolated.xs} 80vw, ${queriesIsolated.sm} 60vw, 30vw`}
                                    priority={index < 2}
                                    loading={index < 2 ? 'eager' : 'lazy'}
                                    alt={alt ?? ''}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            </StyledImageClicker>
                        ))}
                    </>
                ) : (
                    <>
                        {[...Array(2).keys()].map((i) => (
                            <ConditionalWrap
                                key={i}
                                if={i > 0}
                                wrap={(children) => (
                                    <Visibility.VisibleFrom md children={children} />
                                )}
                            >
                                <StyledFallbackImageWrapper key={i}>
                                    <ProductImage priority={true} alt="skeleton" />
                                </StyledFallbackImageWrapper>
                            </ConditionalWrap>
                        ))}
                    </>
                )}
            </StyledImageWrapper>

            <Overlay
                open={imageIndex !== null}
                onDismiss={onOverlayDismiss}
                variant="full"
                onLoad={onOverlayLoad}
                ref={overlayRef}
            >
                <StyledEnlargedImageWrapper enlargeImages={enlargeImages}>
                    {images?.map((image) => (
                        <StyledImageClicker
                            aria-label={image.label}
                            key={image.url}
                            onClick={(event) => {
                                if (!isSmallDevice) {
                                    setScrollPosition(event);
                                    setEnlargeImages(!enlargeImages);
                                }
                            }}
                            cursorType={enlargeImages ? 'mini' : 'maxi'}
                        >
                            <ProductImage
                                key={image.url}
                                image={image}
                                sizes="100vw"
                                skeletonShade="light"
                                alt={alt ?? ''}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        </StyledImageClicker>
                    ))}
                </StyledEnlargedImageWrapper>
            </Overlay>
        </>
    );
};
